import _ from 'lodash';

import {action, observable, reaction, when} from 'mobx';

class PCDealerSelectorStore {
    @observable _initialized = false;
    @observable _dealerSelectorReady = false;

    constructor(baseStore, routerStore) {
        this.changeDealer = this.changeDealer.bind(this);
        this.setDialogMessageCode = this.setDialogMessageCode.bind(this);
        this.attach = this.attach.bind(this);
        this.detach = this.detach.bind(this);

        // create the host element for the dealer selector
        this._selectorElement = document.createElement('div');
        this._selectorElement.addEventListener(
            'dealerSelected',
            action((event) => {
                baseStore.dealer = event.detail;

                // write the new dealer ID onto the route
                const params = _.clone(routerStore.route.params) || {};

                params['dealer-id'] = event.detail.dealerId;
                routerStore.router.navigate(
                    routerStore.route.name,
                    params,
                    {
                        replace: true
                    }
                );
            })
        );
        this._selectorElement.addEventListener(
            'dealerSelectorDialogOpen',
            action((event) => {
                baseStore.dealerSelectorOpen = event.detail;
            })
        );

        // The dealer-selector-ui bundle dispatches this event as soon as it finishes loading,
        // completely independent of our own login state - it must be listened for immediately.
        // Registering it lazily (e.g. only after SSO login resolves) misses the event entirely
        // for the registered-user flow: the Okta redirect round-trip takes several seconds, long
        // after the widget script has already loaded and fired 'dealerSelectorReady', so a
        // listener attached afterward never sees it and dealerSelectorUi.init() is never called.
        document.addEventListener(
            'dealerSelectorReady',
            action(() => {
                this._dealerSelectorReady = true;
            })
        );

        // Initialize the dealer selector once the widget script is ready, the locale is known,
        // AND the initial login attempt has resolved. For a registered (SSO) user the app boots
        // on the `/authenticate` route (no `dealer-id` param) while the auth-code exchange is
        // still in flight, and `userSession.currentUser` is still null - reading them before the
        // login resolves would permanently lock the widget into "no dealer / guest mode" even
        // after the SSO login completes and the router navigates back to the real route with
        // `dealer-id`, because this initialization only runs once.
        when(
            () => this._dealerSelectorReady && baseStore.locale && !baseStore.userSession.loggingIn,
            () => {
                // read the dealer location ID off the route, if it is available
                let initialDealerId = _.get(routerStore, 'route.params.dealer-id');
                const currentUserId = _.get(baseStore.userSession.currentUser, 'id');

                if (currentUserId) {
                    // We will be getting the dealer details via the profile dealers call and
                    // sending them to the dealer selector, no need to have the dealer selector
                    // also get the selected dealer.
                    initialDealerId = undefined;
                }

                dealerSelectorUi.init(
                    this._selectorElement,
                    'header',
                    baseStore.locale,
                    {
                        dealerId: initialDealerId
                    },
                    action(() => {
                        this._initialized = true;
                        dealerSelectorUi.useProfileDealers(currentUserId !== undefined);
                    }),
                    false
                );
            }
        );

        // attach the dealer selector script
        const headElement = document.getElementsByTagName('head')[0];
        const scriptElement = document.createElement('script');

        scriptElement.src = DEALER_SELECTOR_UI_URL;
        headElement.appendChild(scriptElement);

        // set the profile dealers when they become available
        reaction(
            () => {
                return {
                    initialized: this._initialized,
                    cleanProfileDealers: _.get(baseStore.profileDealers, 'cleanProfileDealers')
                };
            },
            (data) => {
                if (!data.initialized || !data.cleanProfileDealers) {
                    return;
                }

                dealerSelectorUi.setProfileDealers(data.cleanProfileDealers);
            }
        );
    }

    attach(element) {
        element.appendChild(this._selectorElement);
    }

    detach() {
        if (!this._selectorElement.parentNode) {
            return;
        }

        this._selectorElement.parentNode.removeChild(this._selectorElement);
    }

    changeDealer() {
        dealerSelectorUi.changeDealer();
    }

    setDialogMessageCode(messageCode) {
        dealerSelectorUi.setDialogMessageCode(messageCode);
    }

    setProfileDealers(profileDealers) {
        dealerSelectorUi.setProfileDealers(profileDealers);
    }

    get initialized() {
        return this._initialized;
    }
}

export default PCDealerSelectorStore;
